import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import ProductComparePreviewSummary from '../../../../components/product-compare-preview-summary/product-compare-preview-summary';
import {
  AngleDownLightIcon,
  AngleUpLightIcon
} from '../../../../shared/icons/icons';

import {
  useOnElementResize,
  useProductComparePreview
} from '../../../../shared/hooks/hooks';

import { getHasInstantPromotions } from '../../../../shared/utils/product/product';

import './product-compare-preview.scss';

const ProductComparePreview = forwardRef(function ProductComparePreview(
  props,
  ref
) {
  const {
    onClearAll,
    onClearProduct,
    productsForComparison,
    maxProductsForComparison
  } = props;

  const hideCompareDescription = maxProductsForComparison < 3;
  const disableCompare = productsForComparison?.length < 2;
  const stickyChildRef = useRef();
  const stickyContainerRef = ref;

  const hasInstantPromotions = getHasInstantPromotions(productsForComparison);
  const { recalculate, isExpanded, setIsExpanded } = useProductComparePreview({
    maxProductsForComparison,
    renderSticky: true,
    stickyChildRef,
    stickyContainerRef
  });

  useOnElementResize(stickyChildRef.current, recalculate);

  return (
    <div ref={stickyContainerRef}>
      <div
        ref={stickyChildRef}
        styleName={!isExpanded ? 'sticky' : 'sticky sticky--expanded'}
      >
        {productsForComparison?.length > 0 && maxProductsForComparison > 0 && (
          <div styleName="compare-preview">
            {maxProductsForComparison > 2 && (
              <Button
                icon={isExpanded ? AngleDownLightIcon : AngleUpLightIcon}
                onClick={() => {
                  setIsExpanded(currentIsExpanded => !currentIsExpanded);
                }}
                styleName="icon"
              />
            )}
            {!hideCompareDescription && (
              <div styleName="compare-description">
                Compare up to {maxProductsForComparison} products
              </div>
            )}
            {[...Array(maxProductsForComparison)].map((_, idx) => (
              <ProductComparePreviewSummary
                hasInstantPromotions={hasInstantPromotions}
                hideDescription={!isExpanded && maxProductsForComparison > 2}
                hideImage={maxProductsForComparison <= 2}
                key={productsForComparison[idx]?.code ?? idx}
                onClearProduct={onClearProduct}
                product={productsForComparison[idx]}
              />
            ))}
            <div styleName="cta-container">
              <div styleName="ctas">
                <Button
                  href={disableCompare ? null : '/compare/product'}
                  isDisabled={disableCompare}
                  isSecondary
                  styleName="compare-btn"
                >
                  Compare
                </Button>
                {isExpanded && (
                  <Button onClick={onClearAll} styleName="clear-all">
                    clear all
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ProductComparePreview.propTypes = {
  maxProductsForComparison: PropTypes.number,
  onClearAll: PropTypes.func,
  onClearProduct: PropTypes.func,
  productsForComparison: PropTypes.arrayOf(PropTypes.object)
};

export default ProductComparePreview;
